import React, { ChangeEventHandler, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { countries } from '@klarpay/enums'
import { Stack, Box, FormHelperText, TextFieldProps } from '@mui/material'
import { isValidPhoneNumber, NationalNumber, parsePhoneNumber } from 'libphonenumber-js/max'

import { find, toLower } from 'lodash'
import { Controller, ControllerProps, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { DEFAULT_COUNTRY_CALLING_CODE } from '../../../constants'
import { Maybe } from '../../../graphql'
import { checkPhone } from './helpers'
import { MenuListComposition } from './MenuListComposition'
import { PhoneInput, PhoneInputWrap, PhonePrefix } from './StyledComponents'

import { prefixCode } from './types'

export const CountryPhoneSelect: FC<
  {
    name: string
    label: string
    countryDefault?: string
    defaultValue?: Maybe<string> | undefined
    componentDisabled?: boolean
    autoClear?: boolean
    skipToastMessage?: boolean
    description?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } & Partial<TextFieldProps & ControllerProps<any>>
> = ({
  name = '',
  label = '',
  countryDefault = DEFAULT_COUNTRY_CALLING_CODE,
  defaultValue = '',
  componentDisabled = false,
  autoClear = false,
  rules,
  skipToastMessage = false,
  description,
  ...rest
}) => {
  const { t } = useTranslation()
  const { control, setValue, errors, setError, clearErrors, watch } = useFormContext()
  const error = errors ? errors[name] : null
  const currentValue = watch(name)
  const fieldCountry = watch('country')

  const [phoneNumberEdited, setPhoneNumberEdited] = useState<NationalNumber | string>(
    defaultValue || '',
  )
  const [countryKey, setCountryKey] = useState<string | undefined>(countryDefault)

  const phonePrefixCount = prefixCode(countryKey).length

  // check selected country
  useEffect(() => {
    if (!fieldCountry) {
      return
    }
    if (!autoClear && !phoneNumberEdited) {
      setCountryKey(fieldCountry.toUpperCase())
    }
    if (autoClear) {
      setCountryKey(fieldCountry.toUpperCase())
      setPhoneNumberEdited('')
      setValue(name, '')
    }
    return () => {
      if (autoClear) {
        setCountryKey('')
        setValue(name, '')
        setPhoneNumberEdited('')
      }
    }
  }, [fieldCountry])

  useEffect(() => {
    if (!!currentValue) {
      try {
        const clearedString = (currentValue as string)
          .replace(/\+/g, (match, offset) => (offset === 0 ? match : ''))
          .replaceAll(' ', '')

        const validNumCheck = isValidPhoneNumber(clearedString)
        const phoneDetails = parsePhoneNumber(clearedString.trim())
        if (validNumCheck) {
          setCountryKey(phoneDetails.country)
          setPhoneNumberEdited(
            phoneDetails.number.replace(`${prefixCode(phoneDetails.country)}`, ''),
          )
        }
      } catch (e) {}
    } else {
      setValue(name, '')
      setPhoneNumberEdited('')
      setCountryKey(DEFAULT_COUNTRY_CALLING_CODE)
    }
  }, [currentValue])

  const countryCode = useMemo(() => {
    const key = toLower(countryKey)
    return find(countries, (c) => c.value.toLowerCase() === key)
  }, [countryKey])

  const handleChangePhoneNumber = useCallback<
    ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  >(
    (value) => {
      if (!!countryKey && countryKey.length > 0) {
        const normalizedNumber = value.target.value.replace(/\D/g, '')
        setPhoneNumberEdited(normalizedNumber)
        if (!checkPhone(prefixCode(countryKey) + ' ' + value.target.value, countryKey)) {
          setError(name, {
            type: 'manual',
            message: t('incorrectPhoneNumberFormat', 'Incorrect phone number format'),
          })
        } else {
          clearErrors(name)
          setValue(name, prefixCode(countryKey) + value.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
      } else {
        setError(name, {
          type: 'manual',
          message: t('countryForPhonePrefixMustSet', 'country for phone prefix must be set'),
        })
      }
    },
    [countryKey, setError, name, t, clearErrors, setValue],
  )

  const handleBlurPhoneNumber = useCallback<
    ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  >(
    (event) => {
      const { value } = event.target
      if (!value) return
      if (!!countryKey && countryKey.length > 0) {
        if (checkPhone(prefixCode(countryKey) + ' ' + value, countryKey)) {
          clearErrors(name)
        } else {
          setValue(name, prefixCode(countryKey) + ' ' + value, {
            shouldDirty: true,
          })
          !skipToastMessage &&
            toast.error(
              t(
                'errorInSavedDataForPhoneNumber',
                'Previously saved value for phone number is not correct',
              ),
            )
        }
      } else {
        setError(name, {
          type: 'manual',
          message: t('countryForPhonePrefixMustSet', 'country for phone prefix must be set'),
        })
      }
    },
    [countryKey, setError, name, t, clearErrors],
  )

  const renderMenuListComposition = useMemo(
    () => (
      <MenuListComposition
        countryCode={countryCode?.value?.toLowerCase()}
        countryKey={countryKey}
        setCountryKey={setCountryKey}
        value={phoneNumberEdited}
        name={name}
        error={!!error}
        componentDisabled={componentDisabled}
      />
    ),
    [countryCode?.value, countryKey, phoneNumberEdited, name, error, componentDisabled],
  )

  return (
    <Box
      mt={3}
      mb={2.5}
      display="flex"
      alignItems="flex-end"
      bgcolor={(theme) => theme.palette.background.paper}
      position="relative"
      sx={{
        '& .MuiInputLabel-formControl': {
          zIndex: 0,
        },
      }}
      data-test={`autotest-${name}`}
    >
      {renderMenuListComposition}
      <Stack flexDirection="row" position="relative" width="100%" alignItems="flex-end">
        <PhonePrefix
          id="prefixCode"
          type="text"
          value={prefixCode(countryKey)}
          error={!!error}
          disabled
          aria-readonly={true}
          data-test={`autotest-${name}PrefixCode`}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              color: componentDisabled ? 'rgba(0, 0, 0, 0.38)' : 'black',
              WebkitTextFillColor: componentDisabled ? 'rgba(0, 0, 0, 0.38)' : 'black',
            },
            flexBasis: phonePrefixCount >= 4 ? 48 : 35,
          }}
        />

        <PhoneInputWrap>
          <Controller
            id="phoneNumberEdited"
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({ onChange, ref }) => {
              return (
                <PhoneInput
                  variant="standard"
                  type="text"
                  value={phoneNumberEdited}
                  onChange={(e) => {
                    onChange(e)
                    handleChangePhoneNumber(e)
                  }}
                  onBlur={handleBlurPhoneNumber}
                  inputRef={ref}
                  label={label}
                  error={!!error}
                  disabled={componentDisabled}
                  data-test="autotest-phoneInput"
                  {...rest}
                />
              )
            }}
          />
        </PhoneInputWrap>
      </Stack>
      {!!error && (
        <FormHelperText
          data-test="errorPhone"
          sx={{
            color: '#EF2828',
          }}
        >
          {error.message}
        </FormHelperText>
      )}
      {description && !error && (
        <FormHelperText
          sx={{
            color: '#262626',
          }}
        >
          {description}
        </FormHelperText>
      )}
    </Box>
  )
}
